import { z } from 'zod';
import { ResourceVersionSchema } from './concurrency.js';
import { paginationQueryShape } from './pagination.js';
/**
 * Contratos de áreas, empleados, roles y usuario actual (docs/migration/09 §6.2 a §6.5).
 * Las propiedades van en camelCase; los instantes, en ISO 8601 UTC.
 */
const Timestamp = z.iso.datetime();
const BooleanParam = z.enum(['true', 'false']).transform((value) => value === 'true');
const SearchParam = z.string().trim().min(2).max(100);
/** Parámetro de ruta `{id}` (UUID). */
export const IdParamsSchema = z.strictObject({ id: z.uuid() });
/** Roles de la aplicación (08 §4.3). El Director General es una condición, no un rol. */
export const AppRoleSchema = z.enum(['ADMINISTRADOR', 'RESPONSABLE', 'EMPLEADO']);
// ---------------------------------------------------------------- Áreas (09 §6.3)
const AreaName = z.string().trim().min(1).max(200);
const AreaDescription = z.string().max(2000);
export const AreaSchema = z.strictObject({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullable(),
    active: z.boolean(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
    version: ResourceVersionSchema,
});
/** Crear un área: nombre no vacío y único sin distinguir mayúsculas; descripción ≤ 2000. */
export const AreaCreateSchema = z.strictObject({
    name: AreaName,
    description: AreaDescription.nullable().optional(),
});
/** Editar o desactivar un área (`PATCH` con `If-Match`). Al menos un campo. */
export const AreaUpdateSchema = z
    .strictObject({
    name: AreaName.optional(),
    description: AreaDescription.nullable().optional(),
    active: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'empty_update' });
export const AreaListQuerySchema = z.strictObject({
    ...paginationQueryShape,
    active: BooleanParam.optional(),
    q: SearchParam.optional(),
});
// ---------------------------------------------------------------- Empleados (09 §6.4)
const Upn = z
    .string()
    .trim()
    .max(320)
    .regex(/^[^@\s]+@[^@\s]+$/);
const Email = z.email().max(320);
const FullName = z.string().trim().min(1).max(200);
const JobTitle = z.string().trim().max(200);
export const EmployeeSchema = z.strictObject({
    id: z.uuid(),
    entraObjectId: z.guid().nullable(),
    upn: z.string(),
    email: z.string(),
    fullName: z.string(),
    jobTitle: z.string().nullable(),
    areaId: z.uuid(),
    managerId: z.uuid().nullable(),
    isDirectorGeneral: z.boolean(),
    active: z.boolean(),
    deactivatedAt: Timestamp.nullable(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
    version: ResourceVersionSchema,
});
/** Resumen para listas de equipo. */
export const EmployeeSummarySchema = z.strictObject({
    id: z.uuid(),
    fullName: z.string(),
    email: z.string(),
    jobTitle: z.string().nullable(),
    areaId: z.uuid(),
    active: z.boolean(),
});
/**
 * Crear un empleado (activo). El Object ID es opcional: sin él, se vincula en el primer inicio de
 * sesión por UPN (fase 5). El Director General no tiene responsable; los demás, sí.
 */
export const EmployeeCreateSchema = z.strictObject({
    entraObjectId: z.guid().nullable().optional(),
    upn: Upn,
    email: Email,
    fullName: FullName,
    jobTitle: JobTitle.nullable().optional(),
    areaId: z.uuid(),
    managerId: z.uuid().nullable().optional(),
    isDirectorGeneral: z.boolean().optional(),
});
/** Editar, cambiar el responsable o desactivar (`PATCH` con `If-Match`). Al menos un campo. */
export const EmployeeUpdateSchema = z
    .strictObject({
    entraObjectId: z.guid().nullable().optional(),
    upn: Upn.optional(),
    email: Email.optional(),
    fullName: FullName.optional(),
    jobTitle: JobTitle.nullable().optional(),
    areaId: z.uuid().optional(),
    managerId: z.uuid().nullable().optional(),
    isDirectorGeneral: z.boolean().optional(),
    active: z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'empty_update' });
export const EmployeeListQuerySchema = z.strictObject({
    ...paginationQueryShape,
    areaId: z.uuid().optional(),
    managerId: z.uuid().optional(),
    active: BooleanParam.optional(),
    q: SearchParam.optional(),
});
/** Problemas señalados en la jerarquía. */
export const HierarchyIssueSchema = z.enum([
    'MANAGER_INACTIVE',
    'AREA_INACTIVE',
    'NO_ACTIVE_ROLE',
    'INACTIVE_WITH_ACTIVE_SUBORDINATES',
]);
/**
 * Nodo del árbol jerárquico, serializado en **preorden**: cada nodo va seguido de sus subordinados;
 * `depth` es 0 para las raíces (sin responsable) y `managerId` enlaza con el padre.
 */
export const HierarchyNodeSchema = z.strictObject({
    id: z.uuid(),
    fullName: z.string(),
    managerId: z.uuid().nullable(),
    areaId: z.uuid(),
    depth: z.int().min(0),
    active: z.boolean(),
    isDirectorGeneral: z.boolean(),
    issues: z.array(HierarchyIssueSchema),
});
// ---------------------------------------------------------------- Roles (09 §6.5)
export const RoleAssignmentSchema = z.strictObject({
    id: z.uuid(),
    employeeId: z.uuid(),
    role: AppRoleSchema,
    active: z.boolean(),
    assignedAt: Timestamp,
    assignedBy: z.uuid().nullable(),
    endedAt: Timestamp.nullable(),
    endedBy: z.uuid().nullable(),
    version: ResourceVersionSchema,
});
export const RoleAssignSchema = z.strictObject({ role: AppRoleSchema });
export const RoleParamsSchema = z.strictObject({ id: z.uuid(), role: AppRoleSchema });
// ---------------------------------------------------------------- Usuario actual (09 §6.2)
/**
 * Perfil propio. `canRequest` = empleado activo, con el rol EMPLEADO vigente y que no es Director
 * General (09 §6.2).
 */
export const MeSchema = z.strictObject({
    id: z.uuid(),
    fullName: z.string(),
    email: z.string(),
    upn: z.string(),
    jobTitle: z.string().nullable(),
    area: z.strictObject({ id: z.uuid(), name: z.string() }),
    roles: z.array(AppRoleSchema),
    isDirectorGeneral: z.boolean(),
    canRequest: z.boolean(),
    manager: z.strictObject({ id: z.uuid(), fullName: z.string() }).nullable(),
});
//# sourceMappingURL=organization.js.map